import React, { createContext, useContext } from "react";
import EditableText from "@/components/EditableText";

/**
 * Context that plumbs the `useOverrides()` result (the `{ get, save, clear }`
 * trio) through the component tree so any nested cell can inline-edit an
 * override without prop-drilling.
 */
export const OverridesContext = createContext({
  get: (_path, fb) => fb,
  save: async () => {},
  clear: async () => {},
});

export function useOverridesCtx() {
  return useContext(OverridesContext);
}

/**
 * `<E>` — the terse editable-text wrapper used by the Results page.
 *
 * Renders `defaultValue` for viewers; for admins it turns into an inline
 * click-to-edit affordance (see `<EditableText>`) that persists overrides via
 * the surrounding OverridesContext. Pass `as="td"` etc. to keep semantics.
 *
 *   <E path={`team.${id}.name`} defaultValue={team.name} />
 */
export function E({ path, defaultValue, as = "span", className = "", multiline = false, emptyLabel = "—", ...rest }) {
  const ctx = useOverridesCtx();
  const override = ctx.get(path, undefined);
  const hasOverride = path && override !== undefined;
  return (
    <EditableText
      path={path}
      defaultValue={defaultValue}
      override={hasOverride ? override : undefined}
      onSave={(v) => ctx.save(path, v)}
      onClear={() => ctx.clear(path)}
      as={as}
      className={className}
      multiline={multiline}
      emptyLabel={emptyLabel}
      {...rest}
    />
  );
}
